/**
 * UI/UX batch C2 R10 — table administration (Create table / Delete table) has its OWN permission reasons. Azure
 * authorizes them with management Actions, `Microsoft.Storage/storageAccounts/tableServices/tables/write` and
 * `…/tables/delete` (Microsoft Learn, "Authorize access to tables with Microsoft Entra ID" — permissions for table
 * service operations): an identity that cannot read entities (no Storage Table Data role) may still create and delete
 * tables as Contributor, and one that reads entities may not. So the entity-read refusal (`dataAccessWriteReason`) is
 * never reused here.
 *
 * Nothing tells the renderer up front whether an identity holds those Actions, so a refusal becomes KNOWN when Azure
 * answers one: a create / delete refused for a permission is recorded per connection and TARGET, and from then on the
 * tree menu, the Tables folder row, the table tab's More menu and the dialogs show the reason — main's explanation
 * (C-N01) — instead of offering an action that will fail. A later success on that target, or a new connect (the account-info generation moves), forgets
 * it. Only a permission refusal is recorded (`isStoragePermissionRefusal`: `AuthorizationPermissionMismatch`,
 * `AuthorizationResourceTypeMismatch`): a SAS outside its allowed IP range, an HTTPS-only SAS over HTTP, an account that
 * disallows Shared Key or a firewall / network `AuthorizationFailure` records nothing (Codex merge-C C-M02) — the cause
 * can clear without a reconnect (the outbound address changes back), so the next attempt must be offered.
 *
 * Scope (Codex C2-01): Azure RBAC assigns roles down to a single table
 * (`…/tableServices/default/tables/<table>`), so tables/delete can be held on table B and not on table A — a delete
 * refusal is table A's own, never the connection's. A table that does not exist yet has no scope of its own, so
 * tables/write for Create must be granted on the table service or the storage account: one create refusal does stand
 * for the whole connection.
 */
import { create } from 'zustand';
import type { Connection } from '../../../../../types/electron';
import type { StorageOperationError } from '../../../../../types/azureStorage';
import { isStorageOperationError } from '../../services/azureStorageApi';
import { isStoragePermissionRefusal } from '../../../../../core/engines/azureStoragePermissionRefusal';
import { useStorageAccountInfoStore } from '../../../../database-objects/store/storageAccountInfoStore';

/** What a create / delete acts on: creating is account-wide (there is no table yet), deleting is one table's. */
export type TableAdminTarget = { op: 'create' } | { op: 'delete'; table: string };

type Refusal = { reason: string; generation: number };
/** Refusals per connection, keyed by `targetKey` ("create", or "delete" + the table name). */
type State = { byConnection: Record<string, Record<string, Refusal>> };

const targetKey = (target: TableAdminTarget): string => (target.op === 'create' ? 'create' : `delete\u0000${target.table}`);

export const useTableAdminRefusalStore = create<State>(() => ({ byConnection: {} }));

/**
 * Codex merge-C C-N01: the reason is MAIN's explanation (`electron/azure-storage/tables/tableAdminErrors.ts`, R10), which
 * knows the credential class the request was sent with — Entra's tables/write or tables/delete at its scope, an account
 * SAS's Container resource type with Create or Write / Delete, a table's own service SAS that can do neither. The
 * renderer used to replace a SAS explanation with "Use a SAS whose permissions include it".
 */
function refusalReason(target: TableAdminTarget, error: StorageOperationError): string {
  const explanation = error.message.trim();
  if (explanation) return explanation;
  return target.op === 'create' ? 'Azure refused to let this connection create a table.' : `Azure refused to let this connection delete the table ${target.table}.`;
}

/** A permission refusal (C-M02: by Azure's code — never an IP range, HTTP, a Shared Key ban or a firewall). */
export function isTableAdminPermissionRefusal(error: unknown): error is StorageOperationError {
  return isStorageOperationError(error) && isStoragePermissionRefusal(error);
}

const generationOf = (connectionId: string): number => useStorageAccountInfoStore.getState().byConnection[connectionId]?.generation ?? 0;

/** Records a create / delete outcome: a permission refusal is remembered, a success forgets that target's refusal. */
export function recordTableAdminOutcome(connection: Pick<Connection, 'id'>, target: TableAdminTarget, error?: unknown): void {
  const refusal = error !== undefined && isTableAdminPermissionRefusal(error) ? error : undefined;
  if (error !== undefined && !refusal) return;
  const key = targetKey(target);
  useTableAdminRefusalStore.setState((state) => {
    const current = { ...state.byConnection[connection.id] };
    if (refusal) current[key] = { reason: `${refusalReason(target, refusal)} Reconnect to try again once that changes.`, generation: generationOf(connection.id) };
    else delete current[key];
    return { byConnection: { ...state.byConnection, [connection.id]: current } };
  });
}

/** The known refusal of this target on this connection, if any (one from an earlier connect is forgotten). */
export function tableAdminRefusal(connectionId: string, target: TableAdminTarget): string | undefined {
  const refusal = useTableAdminRefusalStore.getState().byConnection[connectionId]?.[targetKey(target)];
  return refusal && refusal.generation === generationOf(connectionId) ? refusal.reason : undefined;
}

/** The hook form of `tableAdminRefusal`, for menus and dialogs that must update when a refusal is learnt. */
export function useTableAdminRefusal(connectionId: string, target: TableAdminTarget | undefined): string | undefined {
  const refusal = useTableAdminRefusalStore((state) => (target ? state.byConnection[connectionId]?.[targetKey(target)] : undefined));
  const generation = useStorageAccountInfoStore((state) => state.byConnection[connectionId]?.generation ?? 0);
  return refusal && refusal.generation === generation ? refusal.reason : undefined;
}
